import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { authSelectors } from '../../store/selectors/auth';
import { vocabularySpeechResult, vocabularyUnlock, vocabularyWords } from '../../api/vocabulary';
import { getUserInfoThunk } from '../../store/thunks/profile';
import usePreviewFeature from '../../hooks/usePreviewFeature';
import useSpeech, { RATE_DEFAULT, SLOW_RATE_DEFAULT, voiceLabel } from '../../hooks/useSpeech';
import useSpeechRecognition, { playVerdictSound, primeVerdictSound } from '../../hooks/useSpeechRecognition';

import Head from '../../components/Head/Head';
import Plug from '../../components/plug/plug.component';
import Spinner from '../../common/Spinner/Spinner';
import Button from '../../common/Buttons/Button';
import VocabularyPreview from './VocabularyPreview';
import { SpeakerIcon } from '../../common/SpeakButton/SpeakButton';
import sprite from '../../img/sprites.svg';

import './vocabulary.scss';

const UNLOCK_KEY = 'vocabularyUnlock';

/** Чинний токен тимчасового доступу з localStorage або null. */
const readUnlock = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(UNLOCK_KEY) || 'null');
    if (saved?.u && new Date(saved.exp) > new Date()) return saved;
    localStorage.removeItem(UNLOCK_KEY);
  } catch (e) {
    // localStorage недоступний (приватний режим) — просто без збереження
  }

  return null;
};

// Токен уроку живе не в адресі, а в стані переходу і в localStorage (для оновлення сторінки й нової вкладки)
const LESSON_KEY = 'vocabularyLesson';

const readSavedLesson = () => {
  try {
    return localStorage.getItem(LESSON_KEY);
  } catch (e) {
    return null;
  }
};

const saveLesson = (value) => {
  try {
    if (value) localStorage.setItem(LESSON_KEY, value); else localStorage.removeItem(LESSON_KEY);
  } catch (e) {
    // приватний режим — урок пам'ятається лише до закриття вкладки (через стан переходу)
  }
};

const wordsWord = (count) => {
  const n = Math.abs(count) % 100;
  const n1 = n % 10;
  if (n > 10 && n < 20) return 'слів';
  if (n1 === 1) return 'слово';
  if (n1 >= 2 && n1 <= 4) return 'слова';

  return 'слів';
};

const FEEDBACK_MS = 4000;

const VERDICT_TEXT = {
  ok: 'Правильно!',
  close: 'Майже! Спробуйте ще раз чіткіше.',
  wrong: 'Не схоже. Послухайте слово і спробуйте ще раз.',
  none: 'Ми вас не почули. Натисніть на мікрофон і скажіть слово.',
};

const MicIcon = () => (
  <svg className="vocabulary-card__mic-icon" viewBox="0 0 24 24" aria-hidden="true">
    <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
    <path d="M6 11a6 6 0 0 0 12 0" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M12 17v4M9 21h6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const CheckIcon = () => (
  <svg className="vocabulary-card__check-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5l4.5 4.5L19 7.5" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CrossIcon = () => (
  <svg className="vocabulary-card__check-icon" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M7 7l10 10M17 7L7 17" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
  </svg>
);

const savedStatuses = (words) => (words || []).reduce((acc, item) => {
  if (item.speech_status) acc[item.id] = item.speech_status;

  return acc;
}, {});

const formatRate = (value) => `${Number(value).toFixed(Math.round(Number(value) * 100) % 10 ? 2 : 1)}×`;

/**
 * Сторінка «Слова з озвучкою»: лексика уроку або пробного заняття.
 * Адреса завжди просто `/vocabulary`: токен уроку приходить у стані переходу з іконки навушників,
 * а після оновлення сторінки береться з localStorage. Старі посилання `?w=…` теж працюють.
 */
const SPEAK_CHECK_ENABLED = false;

const Vocabulary = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const token = useSelector(authSelectors).token;
  const isV2 = usePreviewFeature('cabinet-v2');
  const speakCheckPreview = usePreviewFeature('vocabulary-speak-check');
  const canCheck = SPEAK_CHECK_ENABLED && speakCheckPreview;
  const queryW = searchParams.get('w');
  const linkW = location.state?.w || null;
  const w = linkW || queryW || readSavedLesson();

  const [load, setLoad] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // Тимчасовий доступ за паролем, поки сторінка ще не відкрита для всіх
  const [unlock, setUnlock] = useState(readUnlock);
  // Лічильник перезавантажень: зростає, коли треба перепитати сервер (минув таймер / доступ)
  const [reload, setReload] = useState(0);

  const speech = useSpeech(data?.lang);
  const recognition = useSpeechRecognition(data?.lang);
  const [checks, setChecks] = useState({});
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    setChecks(savedStatuses(data?.words));
    setFeedback(null);
  }, [data]);

  useEffect(() => {
    if (!feedback || feedback.verdict === 'ok') return undefined;
    const id = setTimeout(() => setFeedback(null), FEEDBACK_MS);

    return () => clearTimeout(id);
  }, [feedback]);

  const handleCheck = async (item) => {
    if (recognition.listeningId === item.id) {
      recognition.stop();

      return;
    }
    speech.stop();
    primeVerdictSound();
    setFeedback(null);
    const result = await recognition.listen(item);
    if (result.verdict === 'unsupported') return;
    if (result.verdict === 'error' && !result.message) return;
    if (['ok', 'close', 'wrong'].includes(result.verdict)) {
      const verdict = result.verdict === 'ok' ? 'ok' : 'wrong';
      setChecks((prev) => ({ ...prev, [item.id]: prev[item.id] === 'ok' ? 'ok' : verdict }));
      playVerdictSound(result.verdict);
      vocabularySpeechResult({ token, w, wordId: item.id, verdict, heard: result.heard })
        .then((res) => {
          const status = res.data?.data?.speech_status;
          if (status) setChecks((prev) => ({ ...prev, [item.id]: status }));
        })
        .catch(() => {});
    }
    setFeedback({
      id: item.id,
      verdict: result.verdict,
      text: result.message || VERDICT_TEXT[result.verdict] || VERDICT_TEXT.none,
      heard: result.heard,
    });
  };

  useEffect(() => {
    if (token) {
      dispatch(getUserInfoThunk({ token }));
    }
  }, [token]);

  // Старе посилання з `?w=` — переносимо токен у стан сторінки, адреса стає просто /vocabulary
  useEffect(() => {
    if (queryW) navigate('/vocabulary', { replace: true, state: { w: queryW } });
  }, [queryW, navigate]);

  useEffect(() => {
    if (linkW) saveLesson(linkW);
  }, [linkW]);

  // Без уроку сторінці нічого показати — повертаємо на курси.
  useEffect(() => {
    if (!token || queryW) return;
    if (!w) {
      navigate('/courses', { replace: true });

      return;
    }

    setLoad(true);
    setError('');
    vocabularyWords({ token, w, u: unlock?.u })
      .then((res) => {
        if (res.data.info.status) {
          setData(res.data.data);
          if (res.data.data?.locked && unlock) {
            // Сервер не прийняв токен (прострочений) — забуваємо його
            setUnlock(null);
            localStorage.removeItem(UNLOCK_KEY);
          }
        } else {
          setError(res.data.info.message || 'Не вдалося завантажити слова.');
        }
      })
      .catch((err) => {
        const status = err?.response?.status;
        if (!linkW && status === 404) {
          // Збережений урок уже недоступний (інший кабінет, старий токен) — забуваємо і пробуємо без нього
          saveLesson(null);
          navigate('/vocabulary', { replace: true });

          return;
        }
        setError(status === 404
          ? 'Слова до цього заняття не знайдено.'
          : 'Не вдалося завантажити слова. Перевірте інтернет і спробуйте ще раз.');
      })
      .finally(() => setLoad(false));
  }, [w, linkW, queryW, token, unlock, reload, navigate]);

  // Коли 10 хвилин доступу минають — зупиняємо озвучку і знову показуємо заставку
  useEffect(() => {
    if (!unlock) return undefined;
    const ms = new Date(unlock.exp) - Date.now();
    const id = setTimeout(() => {
      speech.stop();
      setUnlock(null);
      localStorage.removeItem(UNLOCK_KEY);
    }, Math.max(0, ms));

    return () => clearTimeout(id);
  }, [unlock]);

  const handleUnlock = useCallback(async (password) => {
    const res = await vocabularyUnlock({ token, password });
    const next = { u: res.data.data.unlock, exp: res.data.data.expires_at };
    try {
      localStorage.setItem(UNLOCK_KEY, JSON.stringify(next));
    } catch (e) {
      // без localStorage доступ живе лише до перезавантаження сторінки
    }
    setUnlock(next);
  }, [token]);

  const handleExpired = useCallback(() => setReload((value) => value + 1), []);

  useEffect(() => {
    if (!settingsOpen) return undefined;
    const onEsc = (e) => e.key === 'Escape' && setSettingsOpen(false);
    document.addEventListener('keydown', onEsc);

    return () => document.removeEventListener('keydown', onEsc);
  }, [settingsOpen]);

  const goBack = () => {
    speech.stop();
    recognition.stop();
    // Відкрито за прямим посиланням — «Назад» не має куди вести, тому на курси
    if (window.history.length > 1) navigate(-1); else navigate('/courses');
  };

  const words = data?.words || [];
  const isReady = !load && !error && data && !data.locked;

  return (
    <section className={`program vocabulary container ${isV2 ? 'vocabulary--v2' : ''}`.trim()}>
      <Head
        textHead={data?.title || 'Слова уроку'}
        subtitle={data?.program_name || undefined}
        goBack={goBack}
        tooltip={isReady ? (
          // Шестерня — у правому верхньому куті, навпроти «Назад»
          <button
            type="button"
            className="vocabulary__gear"
            aria-label="Налаштування озвучки"
            title="Налаштування"
            onClick={() => setSettingsOpen(true)}
          >
            <svg className="icon icon--violet"><use href={`${sprite}#settings`}></use></svg>
          </button>
        ) : undefined}
      />

      {load && <Spinner />}

      {!load && error && <Plug plug={error} icon="file-closed" />}

      {!load && !error && data?.locked && (
        <VocabularyPreview until={data.until} onUnlock={handleUnlock} onExpired={handleExpired} />
      )}

      {isReady && (
        <div className="vocabulary__body">
          {!speech.isSupported && (
            <div className="vocabulary__notice">
              Цей браузер не вміє озвучувати текст. Відкрийте кабінет у Chrome, Safari або Edge —
              або скористайтеся словником у PDF.
            </div>
          )}

          {canCheck && !recognition.isSupported && (
            <div className="vocabulary__notice">
              Перевірка вимови працює в Chrome, Edge або Safari. У цьому браузері доступна лише озвучка слів.
            </div>
          )}

          {speech.isSupported && speech.voicesLoaded && !speech.hasVoice && (
            <div className="vocabulary__notice">
              Схоже, на цьому пристрої немає голосу для мови курсу, тому вимова може бути неточною.
              Якщо звучить неправильно — додайте голос у налаштуваннях телефона («Синтез мовлення»)
              або відкрийте словник на іншому пристрої.
            </div>
          )}

          {settingsOpen && (
            <div className="vocabulary-settings" onClick={(e) => { if (e.target === e.currentTarget) setSettingsOpen(false); }}>
              <div className="vocabulary-settings__card" role="dialog" aria-modal="true" aria-label="Налаштування озвучки">
                <div className="vocabulary-settings__head">
                  <b>Налаштування озвучки</b>
                  {data?.direction_name && <small className="vocabulary-settings__lang"> — {data.direction_name}</small>}
                  <button type="button" className="vocabulary-settings__close" aria-label="Закрити" onClick={() => setSettingsOpen(false)}>
                    <svg className="icon"><use href={`${sprite}#close`}></use></svg>
                  </button>
                </div>

                <label className="vocabulary__field">
                  <span>Швидкість: <b>{formatRate(speech.rate)}</b></span>
                  <input
                    id="vocabularyRate"
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={speech.rate}
                    onChange={(e) => speech.setRate(Number(e.target.value))}
                  />
                </label>

                <label className="vocabulary__field">
                  <span>Швидкість «🐢 повільно»: <b>{formatRate(speech.slowRate)}</b></span>
                  <input
                    id="vocabularySlowRate"
                    type="range"
                    min="0.1"
                    max="1"
                    step="0.05"
                    value={speech.slowRate}
                    onChange={(e) => speech.setSlowRate(Number(e.target.value))}
                  />
                  {speech.slowRate < 0.5 && (
                    <small className="vocabulary__hint">
                      Голоси Google не читають повільніше за 0.5× — фрази з кількох слів ми сповільнюємо паузами
                      між словами. Одне слово повільніше за 0.5× цей голос не прочитає.
                    </small>
                  )}
                </label>

                {speech.voices.length > 1 && (
                  <label className="vocabulary__field">
                    <span>Голос для слів</span>
                    <select
                      id="vocabularyVoice"
                      value={speech.voiceName}
                      onChange={(e) => speech.selectVoice(e.target.value)}
                    >
                      {speech.voices.map((voice) => (
                        <option key={voice.name} value={voice.name}>{voiceLabel(voice)}</option>
                      ))}
                    </select>
                  </label>
                )}

                <div className="vocabulary-settings__foot">
                  {(speech.rate !== RATE_DEFAULT || speech.slowRate !== SLOW_RATE_DEFAULT) && (
                    <button
                      type="button"
                      className="vocabulary__reset"
                      onClick={() => { speech.setRate(RATE_DEFAULT); speech.setSlowRate(SLOW_RATE_DEFAULT); }}
                    >
                      Скинути швидкість
                    </button>
                  )}
                  <Button buttonBg="green" loading="" onClick={() => setSettingsOpen(false)}>Готово</Button>
                </div>
              </div>
            </div>
          )}

          {isV2 && words.length > 0 && (
            <div className="vocabulary__summary">
              <span className="vocabulary__count">{words.length} {wordsWord(words.length)}</span>
              <span className="vocabulary__tip">
                Натисніть на динамік, щоб почути слово, або на черепаху — щоб почути повільно.
                {canCheck && recognition.isSupported && ' Натисніть на мікрофон і скажіть слово — ми перевіримо вимову.'}
              </span>
            </div>
          )}

          <div className="vocabulary__list">
            {words.map((item) => {
              const isSpeaking = speech.speakingId === item.id;
              const isListening = recognition.listeningId === item.id;
              const isChecked = canCheck && checks[item.id] === 'ok';
              const isWrong = canCheck && checks[item.id] === 'wrong';
              const cardFeedback = feedback?.id === item.id ? feedback : null;
              const cardClass = [
                'vocabulary-card',
                isSpeaking ? 'vocabulary-card--speaking' : '',
                isListening ? 'vocabulary-card--listening' : '',
                isChecked ? 'vocabulary-card--checked' : '',
                canCheck && recognition.isSupported ? 'vocabulary-card--check' : '',
              ].filter(Boolean).join(' ');

              return (
                <div className={cardClass} key={item.id}>
                  <div className="vocabulary-card__text">
                    <div className="vocabulary-card__word">
                      {item.word}
                      {isChecked && (
                        <span className="vocabulary-card__check" title="Вимову зараховано">
                          <CheckIcon />
                        </span>
                      )}
                      {isWrong && !isListening && (
                        <span className="vocabulary-card__check vocabulary-card__check--wrong" title="Минулого разу не вийшло — спробуйте ще раз">
                          <CrossIcon />
                        </span>
                      )}
                    </div>
                    {item.transcription && (
                      <div className="vocabulary-card__transcription">{item.transcription}</div>
                    )}
                    <div className="vocabulary-card__translation">{item.translation}</div>
                  </div>

                  <div className="vocabulary-card__controls">
                    <button
                      type="button"
                      className="vocabulary-card__play"
                      aria-label={`Озвучити: ${item.word}`}
                      disabled={!speech.isSupported}
                      onClick={() => speech.speakWord(item)}
                    >
                      <SpeakerIcon speaking={isSpeaking} />
                    </button>
                    <button
                      type="button"
                      className="vocabulary-card__slow"
                      aria-label={`Озвучити повільно: ${item.word}`}
                      title="Повільно"
                      disabled={!speech.isSupported}
                      onClick={() => speech.speakWord(item, { slow: true })}
                    >
                      <span aria-hidden="true">🐢</span>
                    </button>
                    {canCheck && recognition.isSupported && (
                      <button
                        type="button"
                        className="vocabulary-card__mic"
                        aria-label={isListening ? 'Зупинити запис' : `Сказати слово: ${item.word}`}
                        aria-pressed={isListening}
                        title={isListening ? 'Слухаємо…' : 'Сказати слово'}
                        onClick={() => handleCheck(item)}
                      >
                        <MicIcon />
                      </button>
                    )}
                  </div>
                  {isListening && (
                    <div className="vocabulary-card__feedback vocabulary-card__feedback--listening" aria-live="polite">
                      <span className="vocabulary-card__level" aria-hidden="true">
                        {[1, 2, 3, 4, 5].map((bar) => (
                          <i key={bar} className={recognition.level >= bar / 6 ? 'vocabulary-card__level-on' : ''} />
                        ))}
                      </span>
                      {recognition.isMicOn ? 'Слухаємо… скажіть слово' : 'Вмикаємо мікрофон…'}
                    </div>
                  )}
                  {!isListening && cardFeedback && (
                    <div className={`vocabulary-card__feedback vocabulary-card__feedback--${cardFeedback.verdict}`} aria-live="polite">
                      <span>{cardFeedback.text}</span>
                      {cardFeedback.heard && cardFeedback.verdict !== 'ok' && (
                        <span className="vocabulary-card__heard"> Ми почули: «{cardFeedback.heard}»</span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
};

export default Vocabulary;
